import React, { useState, useEffect } from "react"; // 1. Added useEffect
import { FiEdit2, FiTrash2, FiCheck, FiX } from "react-icons/fi";
import toast from 'react-hot-toast';
import { decryptAES } from "../utils/cryptoUtils"; // 2. Added decrypt import

function ChatSidebar({
  user,
  chatList,
  currentChatId,
  onSelectChat,
  onNewChat,
  onRenameChat,
  onDeleteChat,
  setSidebarOpen,
  sidebarOpen,
}) {
  const [editingId, setEditingId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  
  // 3. Added state for the decrypted list
  const [decryptedChatList, setDecryptedChatList] = useState([]);

  // 4. Added the useEffect hook to decrypt titles
  useEffect(() => {
    const decryptTitles = async () => {
      if (!chatList || chatList.length === 0) {
        setDecryptedChatList([]);
        return;
      }
      const sessionsWithDecryptedTitles = await Promise.all(
        chatList.map(async (chat) => {
          const decryptedTitle = await decryptAES(chat.title);
          return { ...chat, title: decryptedTitle || "Untitled Chat" };
        })
      );
      setDecryptedChatList(sessionsWithDecryptedTitles);
    };
    decryptTitles();
  }, [chatList]);

  const sanitizeInput = (str) => {
    const temp = document.createElement("div");
    temp.textContent = str;
    return temp.innerHTML;
  };

  const handleRename = (chatId, title) => {
    const isFreePlan = user?.plan === 'free';
    if (isFreePlan) {
      toast.error("Custom chat titles are a professional feature.");
    } else {
      setEditingId(chatId);
      setNewTitle(title);
    }
  };

  const handleRenameSubmit = async (e, chatId) => {
    e.preventDefault();
    const trimmed = newTitle.trim();
    if (!trimmed || trimmed.length < 1 || trimmed.length > 100) {
      toast.error("Title must be between 1 and 100 characters.");
      return;
    }
    const safeTitle = sanitizeInput(trimmed);
    try {
      await onRenameChat(chatId, safeTitle);
    } catch (err) {
      console.error("Rename failed:", err);
      toast.error("Failed to rename chat.");
    } finally {
      setEditingId(null);
      setNewTitle("");
    }
  };

  const confirmDelete = (chatId) => {
    if (window.confirm("Are you sure you want to delete this chat?")) {
      onDeleteChat(chatId);
    }
  };

  return (
    <div
      className={`
        fixed inset-y-0 left-0 z-40 w-72 sm:w-64 h-full bg-black text-white
        transition-transform duration-300 ease-in-out transform
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:relative lg:translate-x-0
      `}
    >
      <div className="h-full flex flex-col p-3 sm:p-4">
        <div className="md:hidden mb-3">
          <div className="relative group">
<div className="absolute inset-0 rounded-lg shadow-[0_0_8px_rgba(168,85,247,0.3)] group-hover:shadow-[0_0_12px_rgba(168,85,247,0.4)] transition"></div>
            <button
              onClick={() => window.location.href = "/"}
              className="relative w-full text-left px-3 py-2 rounded bg-gray-900 hover:bg-gray-800 text-sm"
            >
              ← Home
            </button>
          </div>
        </div>

        <div className="flex-none">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 text-center sm:text-left">My Conversations</h2>
          {/* This is your original, fully functional button restored */}
          <div className="relative group">
<div className="absolute inset-0 rounded-lg shadow-[0_0_8px_rgba(168,85,247,0.3)] group-hover:shadow-[0_0_12px_rgba(168,85,247,0.4)] transition"></div>
            <button
              onClick={onNewChat}
              className="relative w-full bg-gray-900 text-white py-2 rounded-lg transition"
            >
              + New Chat
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden mt-4 space-y-2">
          {/* This now maps over the decrypted list */}
          {decryptedChatList.map((chat) => (
            <div key={chat.id} className="relative">
              {(chat.id === currentChatId || editingId === chat.id) && (
<div className="absolute inset-0 rounded-lg shadow-[0_0_8px_rgba(168,85,247,0.3)] group-hover:shadow-[0_0_12px_rgba(168,85,247,0.4)] transition"></div>
              )}
              <div
                className={`relative p-3 rounded-lg transition-colors duration-200 ${
                  chat.id === currentChatId || editingId === chat.id
                    ? 'bg-gray-900'
                    : 'bg-black hover:bg-gray-900/50'
                }`}
              >
                {editingId === chat.id ? (
                  <form
                    onSubmit={(e) => handleRenameSubmit(e, chat.id)}
                    className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2"
                  >
                    <input
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      className="w-full bg-black/50 text-white p-2 rounded border border-purple-400/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      maxLength={100}
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end sm:justify-start">
                        <button type="submit" className="text-green-400 hover:text-green-300" title="Save"><FiCheck /></button>
                        <button type="button" onClick={() => setEditingId(null)} className="text-red-400 hover:text-red-300" title="Cancel"><FiX /></button>
                    </div>
                  </form>
                ) : (
                  <div className="flex justify-between items-center">
                    <span
                      onClick={() => {
                        onSelectChat(chat.id);
                        if (window.innerWidth < 1024) setSidebarOpen(false);
                      }}
                      className="flex-grow truncate cursor-pointer"
                      title={chat.title}
                    >
                      {chat.title}
                    </span>
                    <div className="flex space-x-2 ml-2 opacity-60 hover:opacity-100 transition-opacity">
                      <FiEdit2
                        className="cursor-pointer hover:text-yellow-300 hover:drop-shadow-[0_0_4px_rgba(253,224,71,0.7)]"
                        onClick={() => handleRename(chat.id, chat.title)}
                      />
                      <FiTrash2
                        className="cursor-pointer hover:text-red-400 hover:drop-shadow-[0_0_4px_rgba(248,113,113,0.7)]"
                        onClick={() => confirmDelete(chat.id)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ChatSidebar;